import type { ReceiptPayload } from '../types';
import type { PrinterModelId } from '../printerModelConfig';
import { formatThermalMoney } from '../formatThermalMoney';
import {
  fitLineToWidth,
  getReceiptLineWidthForModel,
  getReceiptRasterDotsPerLine,
  itemDetailLines,
  separatorDashes,
} from '../receiptLayout';
import {
  ESC,
  LF,
  appendEscPosJobFlush,
  appendEscPosJobFlushIssc,
  cmdAlign,
  cmdBold,
  cmdCut,
  cmdFeedLines,
  cmdInit,
  cmdQrCode,
  concatBytes,
  encodeUtf8,
} from './commands';
import { encodeLogoRasterEscPos } from './rasterLogo';

export type EscPosProfile = 'default' | 'xp-p203a' | 'issc' | 'issc-cut';

export type EncodeReceiptOptions = {
  lineWidth?: number;
  profile?: EscPosProfile;
  printerModelId?: PrinterModelId;
  /** Overrides the model's logo raster width (desktop printers configured for 58 mm paper). */
  rasterDotsPerLine?: number;
};

function padLine(left: string, right: string, width: number): string {
  const maxLeft = Math.max(4, width - right.length - 1);
  let l = left;
  if (l.length > maxLeft) l = `${l.slice(0, maxLeft - 1)}…`;
  const spaces = Math.max(1, width - l.length - right.length);
  return `${l}${' '.repeat(spaces)}${right}`;
}

function appendBrandingText(
  parts: Uint8Array[],
  branding: ReceiptPayload['brandingHeader'],
  lineWidth: number,
  lineEnd: string,
): void {
  if (!branding?.lines?.length) return;
  for (const line of branding.lines) {
    const trimmed = line.trim();
    if (trimmed) parts.push(encodeUtf8(`${fitLineToWidth(trimmed, lineWidth)}${lineEnd}`));
  }
  parts.push(encodeUtf8(`${separatorDashes(lineWidth)}${lineEnd}`));
  parts.push(cmdFeedLines(1));
}

/**
 * Build ESC/POS receipt bytes (async when logo URL present).
 * Width and centering follow the selected printer model.
 */
export async function encodeReceipt(
  r: ReceiptPayload,
  lineWidthOrOpts: number | EncodeReceiptOptions = 32,
  legacyProfile: EscPosProfile = 'default',
): Promise<Uint8Array> {
  const opts: EncodeReceiptOptions =
    typeof lineWidthOrOpts === 'object'
      ? lineWidthOrOpts
      : { lineWidth: lineWidthOrOpts, profile: legacyProfile };

  const modelId = opts.printerModelId ?? 'xp-p203a';
  const lineWidth = opts.lineWidth ?? getReceiptLineWidthForModel(modelId);
  const profile = opts.profile ?? (modelId === 'xp-p203a' ? 'xp-p203a' : 'default');
  const p203a = profile === 'xp-p203a';
  const isscLine = profile === 'issc';
  const isscCut = profile === 'issc-cut';
  const issc = isscLine || isscCut;
  const lineEnd = issc ? '\r\n' : '\n';
  const dotsPerLine = opts.rasterDotsPerLine ?? getReceiptRasterDotsPerLine(modelId);

  const parts: Uint8Array[] = [];
  const currency = r.currency || 'LKR';

  parts.push(cmdInit());
  if (p203a || isscCut) {
    parts.push(new Uint8Array([ESC, 0x74, 0x00]));
  }

  parts.push(cmdAlign(1));

  if (r.brandingHeader?.logoUrl) {
    const raster = await encodeLogoRasterEscPos(r.brandingHeader.logoUrl, dotsPerLine);
    if (raster) {
      parts.push(raster);
      parts.push(cmdFeedLines(1));
    }
  }

  appendBrandingText(parts, r.brandingHeader, lineWidth, lineEnd);

  parts.push(cmdBold(true));
  parts.push(encodeUtf8(`${fitLineToWidth(r.title ?? 'RECEIPT', lineWidth)}${lineEnd}`));
  parts.push(cmdBold(false));
  parts.push(cmdFeedLines(1));

  parts.push(cmdAlign(0));

  if (r.headerLines?.length) {
    for (const line of r.headerLines) {
      parts.push(encodeUtf8(`${fitLineToWidth(line, lineWidth)}${lineEnd}`));
    }
    parts.push(cmdFeedLines(1));
  }

  if (r.meta?.length) {
    for (const row of r.meta) {
      parts.push(encodeUtf8(`${padLine(row.left, row.right, lineWidth)}${lineEnd}`));
    }
    parts.push(cmdFeedLines(1));
  }

  if (r.items?.length) {
    for (const item of r.items) {
      const left = item.qty != null ? `${item.label} x${item.qty}` : item.label;
      const right =
        item.total != null
          ? formatThermalMoney(item.total, currency)
          : item.unitPrice != null
            ? formatThermalMoney(item.unitPrice, currency)
            : '';
      parts.push(encodeUtf8(`${padLine(left, right, lineWidth)}${lineEnd}`));
      for (const detail of itemDetailLines(item.details, lineWidth)) parts.push(encodeUtf8(`${detail}${lineEnd}`));
    }
    parts.push(cmdFeedLines(1));
  }

  if (r.subtotal != null) {
    parts.push(encodeUtf8(`${padLine('Subtotal', formatThermalMoney(r.subtotal, currency), lineWidth)}${lineEnd}`));
  }
  if (r.tax != null) {
    parts.push(encodeUtf8(`${padLine('Tax', formatThermalMoney(r.tax, currency), lineWidth)}${lineEnd}`));
  }

  parts.push(cmdBold(true));
  parts.push(encodeUtf8(`${padLine('TOTAL', formatThermalMoney(r.total, currency), lineWidth)}${lineEnd}`));
  parts.push(cmdBold(false));
  parts.push(cmdFeedLines(1));

  parts.push(cmdAlign(1));

  if (r.footerLines?.length) {
    for (const line of r.footerLines) {
      parts.push(encodeUtf8(`${fitLineToWidth(line, lineWidth)}${lineEnd}`));
    }
  }

  if (r.printedAt) {
    parts.push(encodeUtf8(`${lineEnd}${fitLineToWidth(r.printedAt, lineWidth)}${lineEnd}`));
  }

  parts.push(encodeUtf8(issc ? '\r\n\r\n' : '\n'));

  if (r.qrPayload?.trim()) {
    parts.push(cmdQrCode(r.qrPayload.trim()));
    parts.push(new Uint8Array([LF, LF]));
  }

  if (isscCut) {
    appendEscPosJobFlush(parts, 6);
  } else if (issc) {
    appendEscPosJobFlushIssc(parts, 10);
  } else if (p203a) {
    appendEscPosJobFlush(parts, 6);
  } else {
    parts.push(cmdFeedLines(3));
    parts.push(cmdCut());
  }

  return concatBytes(...parts);
}

/** Minimal centered text slip (init, lines, feed, cut) — for connectivity tests. */
export async function encodePlainTextNotes(
  lines: string[],
  profile: EscPosProfile = 'default',
  printerModelId: PrinterModelId = 'xp-p203a',
): Promise<Uint8Array> {
  const lineWidth = getReceiptLineWidthForModel(printerModelId);
  return encodeReceipt(
    {
      title: lines[0] ?? 'TEST',
      total: 0,
      currency: 'LKR',
      headerLines: lines.slice(1),
    },
    { lineWidth, profile, printerModelId },
  );
}

/** Dedicated XP-P203A connectivity slip. */
export async function encodeXpP203aTestReceipt(): Promise<Uint8Array> {
  return encodePlainTextNotes(
    ['XP-P203A TEST', 'Bluetooth OK', 'ESC/POS OK'],
    'xp-p203a',
    'xp-p203a',
  );
}

/**
 * Minimal ISSC UART test slip: init, plain CRLF lines, feed, full cut (no ESC t 0 / bold / center).
 */
export function encodeIsscXpP203aTestReceipt(): Uint8Array {
  const parts: Uint8Array[] = [];
  parts.push(cmdInit());
  parts.push(encodeUtf8('XP-P203A TEST\r\n'));
  parts.push(encodeUtf8('Bluetooth OK\r\n'));
  parts.push(encodeUtf8('ESC/POS OK\r\n'));
  parts.push(encodeUtf8('\r\n\r\n'));
  appendEscPosJobFlushIssc(parts, 10);
  return concatBytes(...parts);
}

/** ISSC BLE when credits never arrive: CRLF lines + full cut (cat-printer style flush). */
export function encodeIsscXpP203aTestReceiptWithCut(): Uint8Array {
  const parts: Uint8Array[] = [];
  parts.push(cmdInit());
  parts.push(encodeUtf8('XP-P203A TEST\r\n'));
  parts.push(encodeUtf8('Bluetooth OK\r\n'));
  parts.push(encodeUtf8('ESC/POS OK\r\n'));
  parts.push(encodeUtf8('\r\n\r\n'));
  appendEscPosJobFlush(parts, 6);
  return concatBytes(...parts);
}

/** Smallest possible ISSC slip: init + one line + feed (no cut). */
export function encodeIsscMinimalFeedTest(): Uint8Array {
  const parts: Uint8Array[] = [];
  parts.push(cmdInit());
  parts.push(encodeUtf8('TEST\r\n\r\n'));
  appendEscPosJobFlushIssc(parts, 6);
  return concatBytes(...parts);
}
